// The one AGENT map (#5201, epic #5200 D10) — which platform agent answers for every
// routing purpose, every agent mode and every task origin. It lives here, not in the
// dashboard, so every consumer (the Agents page, its activity read, the model picker's
// `reservedFor`) reads ONE mapping and none keeps a private copy that drifts.
//
// THREE EXHAUSTIVE RECORDS, and the exhaustiveness is the point: each is typed
// `Record<Vocabulary, …>`, so a purpose, mode or origin added to its vocabulary is a
// type error HERE until someone decides which agent owns it. That only holds for the
// purpose record because it is keyed on `LlmRoutePurposeAny` (all of
// `LLM_ROUTE_PURPOSES`), never `LlmRoutePurpose`, which names just the non-job-class
// extras and would let a new `JobClass` through unassigned.
//
// Where an origin runs no model of its own the record says WHY, as a machine code
// (`AgentNoneReason`) — never English. The page renders it through `t()` like every
// other user-facing string.

import type { AgentModeKey } from '../entities/agent-mode';
import type { LlmRoutePurposeAny } from './job-class-routing';
import type { TaskOrigin } from './task-envelope';

/** A platform agent, as the Agents page names it. Stored nowhere: a UI and read-model key. */
export const AgentId = {
  Coder: 'coder',
  Reviewer: 'reviewer',
  CiFixer: 'ci_fixer',
  Planner: 'planner',
  Scout: 'scout',
  Devops: 'devops',
  Gardener: 'gardener',
  Escalation: 'escalation',
  /** The triage judge (#6388) — owns the `triage` pin; unpinned, it runs on the Coder's route. */
  Triage: 'triage',
  /** The intake brief screen (#6938) — owns the `screen` pin; never the `default` row. */
  Screen: 'screen',
  Default: 'default',
} as const;
export type AgentId = (typeof AgentId)[keyof typeof AgentId];
/** Every {@link AgentId}, in declaration order. */
export const AGENT_IDS: readonly AgentId[] = Object.values(AgentId);

/** Why an origin maps to no agent's model — a code the UI maps to `agents.none.<code>`. */
export const AgentNoneReason = {
  /** `pr_babysit_watch` is born terminal and never claimed: it holds watch state, not a run. */
  NeverRunsAModel: 'never_runs_a_model',
  /** `setup` onboards a repo on the Coder's model (it routes as `general`), not one of its own. */
  OnboardingUsesCoderModel: 'onboarding_uses_coder_model',
} as const;
export type AgentNoneReason = (typeof AgentNoneReason)[keyof typeof AgentNoneReason];

/** An origin's owner: an agent, or the reason none owns it. */
export type AgentOriginBinding = AgentId | { readonly none: AgentNoneReason };

export type AgentRoster = {
  /** Which agent owns each `llm_routes` pin — one picker per purpose, owned by this agent. */
  readonly purpose: Readonly<Record<LlmRoutePurposeAny, AgentId>>;
  /**
   * Which agent each on/off mode switch (`agent_modes`) belongs to — every stored key,
   * including the ones whose org/repo door the write matrix has not opened
   * (`AGENT_MODE_WRITABLE`). A reader that shows or folds a switch iterates the keys its
   * scope can write (`agentModeWritable`), never this record whole. One switch, one agent:
   * CI Fixer has its own `ci_fixer` switch since #7390, and no switch governs a second agent.
   */
  readonly mode: Readonly<Record<AgentModeKey, AgentId>>;
  /** Which agent's work each task origin is. */
  readonly origin: Readonly<Record<TaskOrigin, AgentOriginBinding>>;
  /**
   * An agent with no purpose of its own, and the agent whose model it runs on. DevOps
   * routes as `general` (`jobClassForEnvelope`), so it uses the Coder's model (D5).
   */
  readonly modelFrom: Readonly<Partial<Record<AgentId, AgentId>>>;
};

export const AGENT_ROSTER: AgentRoster = {
  purpose: {
    general: AgentId.Coder,
    coding: AgentId.Coder,
    quick: AgentId.Coder,
    'debugging-qa': AgentId.Coder,
    review: AgentId.Reviewer,
    'ci-triage': AgentId.CiFixer,
    plan: AgentId.Planner,
    scout: AgentId.Scout,
    gardener: AgentId.Gardener,
    escalation: AgentId.Escalation,
    triage: AgentId.Triage,
    screen: AgentId.Screen,
    default: AgentId.Default,
  },
  mode: {
    worker: AgentId.Coder,
    devops: AgentId.Devops,
    scout: AgentId.Scout,
    reviewer: AgentId.Reviewer,
    ci_fixer: AgentId.CiFixer,
    planner: AgentId.Planner,
    triage: AgentId.Triage,
    gardener: AgentId.Gardener,
    escalation: AgentId.Escalation,
  },
  origin: {
    gh_issue: AgentId.Coder,
    sentry: AgentId.Coder,
    linear: AgentId.Coder,
    manual: AgentId.Coder,
    conversation: AgentId.Coder,
    pr_review: AgentId.Reviewer,
    pr_review_reply: AgentId.Reviewer,
    pr_babysit: AgentId.CiFixer,
    pr_babysit_watch: { none: AgentNoneReason.NeverRunsAModel },
    scout: AgentId.Scout,
    setup: { none: AgentNoneReason.OnboardingUsesCoderModel },
    devops: AgentId.Devops,
    // #6759: a model call offered to a machine is its caller's work.
    triage_judge: AgentId.Triage,
    kb_fold: AgentId.Gardener,
    // #7569: the Planner's plan call. Bound now, but the Planner stays Not in use and
    // queue-less until a build can claim it (`agentClaimableOrigins`, #6765).
    plan_author: AgentId.Planner,
  },
  modelFrom: {
    devops: AgentId.Coder,
  },
};

/**
 * WHICH ON/OFF SWITCH HOLDS each task origin (#7390) — the `agent_modes.mode_key` the claim
 * gate (`taskModeKeySql`, `@developerz/db`) holds a task under and the assigner resolves its
 * `agent_mode` by, or `null` for an origin no switch holds (`setup`: onboarding is not a
 * mode). Exhaustive over `TaskOrigin`, so a new origin is a type error here until it names
 * its key or `null`.
 *
 * NOT the prose selector. `promptRoleForEnvelope` still picks which stored prose a run reads
 * (a `pr_babysit` round reads worker prose); this table says only which switch holds it. It
 * replaced the derivation from that selector, which held a babysit round under the Coder's
 * `worker` switch. Every non-null value whose origin an agent owns (`AGENT_ROSTER.origin`) is
 * that agent's own switch, pinned by `agent-mode-key-for-origin.test.ts`. The one non-null
 * value with no owning agent is `pr_babysit_watch` (`{ none: NeverRunsAModel }`: it holds
 * watch state and never runs a model); it rides `ci_fixer` because it is CI Fixer's lane.
 */
export const AGENT_MODE_KEY_FOR_ORIGIN: Readonly<Record<TaskOrigin, AgentModeKey | null>> = {
  conversation: 'worker',
  gh_issue: 'worker',
  sentry: 'worker',
  linear: 'worker',
  manual: 'worker',
  pr_review: 'reviewer',
  pr_review_reply: 'reviewer',
  pr_babysit: 'ci_fixer',
  pr_babysit_watch: 'ci_fixer',
  scout: 'scout',
  devops: 'devops',
  setup: null,
  // #6759: the caller's own switch holds its machine offer.
  triage_judge: 'triage',
  kb_fold: 'gardener',
  // #7569: the Planner's own switch holds its plan call.
  plan_author: 'planner',
};
